// Construct from parts — no Date object, no UTC conversion.
export const calendarDate = (year, month, day) => {
    const y = String(year).padStart(4, "0");
    const m = String(month).padStart(2, "0");
    const d = String(day).padStart(2, "0");
    return `${y}-${m}-${d}`;
};
const ISO_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
export const parseCalendarDate = (s) => {
    if (!ISO_RE.test(s))
        return null;
    const [, y, m, d] = ISO_RE.exec(s);
    const year = Number(y), month = Number(m), day = Number(d);
    if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month))
        return null;
    return s;
};
const parts = (cd) => {
    const [y, m, d] = cd.split("-").map(Number);
    return [y, m, d];
};
// Days in a month — no Date object, no UTC.
const daysInMonth = (year, month) => {
    if (month === 2) {
        const leap = (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0;
        return leap ? 29 : 28;
    }
    return [0, 31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month];
};
export const compare = (a, b) => 
// ISO-8601 strings are lexicographically ordered — valid because year/month/day
// are zero-padded to fixed widths and ordered most-to-least significant.
a < b ? -1 : a > b ? 1 : 0;
export const isBefore = (a, b) => compare(a, b) < 0;
export const isAfterOrEqual = (a, b) => compare(a, b) >= 0;
export const addDays = (cd, n) => {
    let [year, month, day] = parts(cd);
    day += n;
    // Normalize forward
    while (day > daysInMonth(year, month)) {
        day -= daysInMonth(year, month);
        month++;
        if (month > 12) {
            month = 1;
            year++;
        }
    }
    // Normalize backward
    while (day < 1) {
        month--;
        if (month < 1) {
            month = 12;
            year--;
        }
        day += daysInMonth(year, month);
    }
    return calendarDate(year, month, day);
};
// Whole days from `a` to `b` (negative when b precedes a). Pure calendar arithmetic
// via a day count from a fixed proleptic epoch — no Date object, no timezone.
export const daysBetween = (a, b) => dayNumber(b) - dayNumber(a);
// Days since 0000-03-01, using the standard civil-from-days algorithm shifted so
// March is the first month (which makes the leap day the last day of the year).
const dayNumber = (cd) => {
    const [y, m, d] = parts(cd);
    const year = m <= 2 ? y - 1 : y;
    const era = Math.floor(year / 400);
    const yoe = year - era * 400;
    const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
    const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
    return era * 146097 + doe;
};
export const addMonths = (cd, n) => {
    let [year, month, day] = parts(cd);
    month += n;
    // Normalize month into 1–12
    while (month > 12) {
        month -= 12;
        year++;
    }
    while (month < 1) {
        month += 12;
        year--;
    }
    // Clamp day to end of target month
    day = Math.min(day, daysInMonth(year, month));
    return calendarDate(year, month, day);
};
export const addYears = (cd, n) => {
    const [year, month, day] = parts(cd);
    const newYear = year + n;
    // Clamp Feb 29 on non-leap years
    const clampedDay = Math.min(day, daysInMonth(newYear, month));
    return calendarDate(newYear, month, clampedDay);
};
// Returns the current date in the given IANA timezone.
// Uses Intl to decompose a UTC instant into local parts — never parses
// the resulting string through `new Date()` to avoid DST/TZ shifts.
export const todayIn = (timezone, now) => {
    const fmt = new Intl.DateTimeFormat("en-CA", {
        timeZone: timezone,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
    });
    // en-CA formats as YYYY-MM-DD
    return fmt.format(now);
};
//# sourceMappingURL=calendarDate.js.map